import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../components/CustomDatePicker';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface RoomType {
  id: string;
  name: string;
  minGuests: number;
  maxGuests: number;
  toilets: number;
  showers: number;
  baths: number;
  floors: string;
}

const roomTypes: RoomType[] = [
  {
    id: '6rooms',
    name: '1棟6室貸切タイプ',
    minGuests: 6,
    maxGuests: 25,
    toilets: 4,
    showers: 2,
    baths: 1,
    floors: '2F・3F・4F・5F'
  },
  {
    id: '4rooms',
    name: '1棟4室貸切タイプ',
    minGuests: 4,
    maxGuests: 15,
    toilets: 3,
    showers: 2,
    baths: 1,
    floors: '2F・3F・4F'
  },
  {
    id: '3rooms',
    name: '1棟3室貸切タイプ',
    minGuests: 3,
    maxGuests: 12,
    toilets: 3,
    showers: 2,
    baths: 1,
    floors: '2F・3F・4F'
  }
];

export default function ReservationPage() {
  const [selectedRoom, setSelectedRoom] = useState<RoomType | null>(null);
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [guestCount, setGuestCount] = useState<number | ''>('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [disabledDates, setDisabledDates] = useState<string[]>([]);

  const roomSelectionRef = useRef<HTMLElement>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // SNS URLs
  const lineUrl = 'https://lin.ee/niVOxO9';
  const wechatId = 'reina017';
  const whatsappNumber = '819075750017';
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  useEffect(() => {
    if (selectedRoom) {
      loadDisabledDates(selectedRoom.id);
    }
  }, [selectedRoom]);

  const loadDisabledDates = async (roomType: string) => {
    try {
      const disabled: string[] = [];

      const { data: blockedDates, error: blockedError } = await supabase
        .from('blocked_dates')
        .select('blocked_date')
        .eq('room_type', roomType);

      if (blockedError) throw blockedError;

      if (blockedDates) {
        blockedDates.forEach((item) => {
          disabled.push(item.blocked_date);
        });
      }

      const { data: reservations, error: reservationsError } = await supabase
        .from('reservations')
        .select('check_in_date, check_out_date')
        .eq('room_type', roomType);

      if (reservationsError) throw reservationsError;

      if (reservations) {
        reservations.forEach((reservation) => {
          const dates = getDateRange(reservation.check_in_date, reservation.check_out_date);
          disabled.push(...dates);
        });
      }

      setDisabledDates([...new Set(disabled)]);
    } catch (error) {
      console.error('Error loading disabled dates:', error);
      setErrorMessage('予約可能日の読み込みに失敗しました。再度お試しください。');
    }
  };

  const getDateRange = (checkIn: string, checkOut: string): string[] => {
    const dates: string[] = [];
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    
    return dates;
  };

  const isDateDisabled = (date: string): boolean => {
    return disabledDates.includes(date);
  };

  const excludedDates = disabledDates.map(dateStr => {
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date;
  });

  const handleCheckInChange = (date: Date | null) => {
    if (!selectedRoom) {
      setErrorMessage('先に部屋タイプを選択してください');
      // 部屋タイプ選択セクションにスクロール
      if (roomSelectionRef.current) {
        roomSelectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!date) return;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    if (isDateDisabled(dateStr)) {
      setErrorMessage('この日付は予約できません');
      return;
    }
    setCheckInDate(date);
    setErrorMessage('');
    
    if (checkOutDate && date >= checkOutDate) {
      setCheckOutDate(null);
    }
  };

  const handleCheckOutChange = (date: Date | null) => {
    if (!selectedRoom) {
      setErrorMessage('先に部屋タイプを選択してください');
      // 部屋タイプ選択セクションにスクロール
      if (roomSelectionRef.current) {
        roomSelectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!date) return;
    
    if (!checkInDate) {
      setErrorMessage('先にチェックイン日を選択してください');
      return;
    }

    // 最小宿泊日数チェック(2泊)
    const minStayDate = new Date(checkInDate.getTime() + 2 * 24 * 60 * 60 * 1000);
    if (date < minStayDate) {
      setErrorMessage('最低2泊からご予約いただけます');
      return;
    }

    const checkInYear = checkInDate.getFullYear();
    const checkInMonth = String(checkInDate.getMonth() + 1).padStart(2, '0');
    const checkInDay = String(checkInDate.getDate()).padStart(2, '0');
    const checkInStr = `${checkInYear}-${checkInMonth}-${checkInDay}`;
    
    const checkOutYear = date.getFullYear();
    const checkOutMonth = String(date.getMonth() + 1).padStart(2, '0');
    const checkOutDay = String(date.getDate()).padStart(2, '0');
    const checkOutStr = `${checkOutYear}-${checkOutMonth}-${checkOutDay}`;
    
    const dateRange = getDateRange(checkInStr, checkOutStr);
    const hasDisabledDate = dateRange.some(d => isDateDisabled(d));
    
    if (hasDisabledDate) {
      setErrorMessage('選択した期間に予約できない日が含まれています');
      return;
    }

    setCheckOutDate(date);
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRoom) {
      setErrorMessage('部屋タイプを選択してください');
      return;
    }

    if (!checkInDate || !checkOutDate) {
      setErrorMessage('チェックイン日とチェックアウト日を選択してください');
      return;
    }

    if (!guestCount || guestCount < selectedRoom.minGuests || guestCount > selectedRoom.maxGuests) {
      setErrorMessage(`人数は${selectedRoom.minGuests}〜${selectedRoom.maxGuests}名の範囲で入力してください`);
      return;
    }

    const checkInYear = checkInDate.getFullYear();
    const checkInMonth = String(checkInDate.getMonth() + 1).padStart(2, '0');
    const checkInDay = String(checkInDate.getDate()).padStart(2, '0');
    const checkInStr = `${checkInYear}-${checkInMonth}-${checkInDay}`;
    
    const checkOutYear = checkOutDate.getFullYear();
    const checkOutMonth = String(checkOutDate.getMonth() + 1).padStart(2, '0');
    const checkOutDay = String(checkOutDate.getDate()).padStart(2, '0');
    const checkOutStr = `${checkOutYear}-${checkOutMonth}-${checkOutDay}`;
    
    const dateRange = getDateRange(checkInStr, checkOutStr);
    const hasDisabledDate = dateRange.some(d => isDateDisabled(d));
    
    if (hasDisabledDate) {
      setErrorMessage('選択した期間に予約できない日が含まれています');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const reservationData = {
        room_type: selectedRoom.id,
        check_in_date: checkInStr,
        check_out_date: checkOutStr,
        guest_count: guestCount,
        guest_name: guestName,
        guest_email: guestEmail,
        guest_phone: guestPhone,
        special_requests: specialRequests,
        status: 'pending'
      };

      const { error } = await supabase.from('reservations').insert([reservationData]);

      if (error) throw error;

      // メール通知を送信
      try {
        await supabase.functions.invoke('send-reservation-email', {
          body: reservationData
        });
      } catch (emailError) {
        console.error('メール送信エラー:', emailError);
      }

      // 確認メールを送信
      try {
        await supabase.functions.invoke('send-confirmation-email', {
          body: {
            ...reservationData,
            language: 'ja'
          }
        });
      } catch (confirmationError) {
        console.error('確認メール送信エラー:', confirmationError);
      }

      // LINE通知を送信
      try {
        await supabase.functions.invoke('send-line-notification', {
          body: reservationData
        });
      } catch (lineError) {
        console.error('LINE通知エラー:', lineError);
      }

      setShowSuccess(true);
      setSelectedRoom(null);
      setCheckInDate(null);
      setCheckOutDate(null);
      setGuestCount('');
      setGuestName('');
      setGuestEmail('');
      setGuestPhone('');
      setSpecialRequests('');
      setDisabledDates([]);

      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('予約の送信に失敗しました。もう一度お試しください');
    } finally {
      setIsSubmitting(false);
    }
  };

  const minCheckOutDate = checkInDate 
    ? new Date(checkInDate.getTime() + 2 * 24 * 60 * 60 * 1000)
    : new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl font-bold mb-4">GuestHouse017大阪</p>
          <h1 className="text-3xl md:text-4xl font-semibold mb-2">
            お問い合わせ<br />
            ご予約フォーム
          </h1>
          <div className="mt-6">
            <p className="text-xl text-teal-50 leading-relaxed">お部屋タイプを選んで</p>
            <p className="text-lg text-teal-50 leading-relaxed">ご希望の日程をお知らせください</p>
          </div>
          <h2 className="text-lg text-teal-100 mt-4">家族旅行・グループ宿泊・団体利用まで、こちらのフォームから24時間いつでもご予約いただけます。</h2>
        </div>
      </header>

      {/* 案内セクション */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {/* セクション①：ご予約の前にご確認ください */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-information-line w-6 h-6 flex items-center justify-center text-teal-500 mr-2"></i>
              <h3 className="text-xl font-bold text-gray-900">ご予約の前にご確認ください</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>当施設はセルフチェックイン方式です。暗証番号はご到着日の前日〜当日にご案内いたします。</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>建物内は全面禁煙です（※喫煙可能エリアは2階、402室、５階ベランダのみ）。</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>ご予約人数と実際のご宿泊人数が異なる場合は、必ず事前にご連絡ください。</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>深夜や早朝の大きな物音・廊下での会話は、近所のご迷惑になりますのでご遠慮ください。</span>
              </li>
            </ul>
          </div>

          {/* セクション②：ご予約の流れ */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-list-ordered w-6 h-6 flex items-center justify-center text-teal-500 mr-2"></i>
              <h3 className="text-xl font-bold text-gray-900">ご予約の流れ</h3>
            </div>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-teal-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">1</span>
                <span>下記フォームに「部屋タイプを選択、ご宿泊日・人数・代表者様情報」をご入力ください。</span>
              </li>
              <li className="flex items-start">
                <span className="bg-teal-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">2</span>
                <span>送信後、自動返信メールが届きます（※届かない場合は迷惑メールフォルダをご確認ください）。</span>
              </li>
              <li className="flex items-start">
                <span className="bg-teal-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">3</span>
                <span>当館にて内容を確認後、「予約確定メール」とチェックインのご案内をお送りいたします。</span>
              </li>
            </ol>
          </div>

          {/* セクション③：お支払いについて */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-bank-card-line w-6 h-6 flex items-center justify-center text-teal-500 mr-2"></i>
              <h3 className="text-xl font-bold text-gray-900">お支払いについて</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>予約サイトまたはクレジットカードでの事前決済となる場合があります。</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>現地決済をご希望の場合は、必ず事前にご相談ください。</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>ご請求金額には清掃料・消費税が含まれます（別途料金がある場合は事前にご案内します）。</span>
              </li>
            </ul>
          </div>

          {/* セクション④：キャンセルポリシー */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-calendar-close-line w-6 h-6 flex items-center justify-center text-teal-500 mr-2"></i>
              <h3 className="text-xl font-bold text-gray-900">キャンセルポリシー</h3>
            </div>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span>ご到着日の<strong>15日前まで</strong>は無料でキャンセル可能</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span><strong>15〜10日前</strong>のキャンセル：宿泊料金の50%</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span><strong>9〜6日前</strong>のキャンセル：宿泊料金の70%</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2 mt-0.5"></i>
                <span><strong>5日前〜当日および不泊</strong>：宿泊料金の100%</span>
              </li>
            </ul>
            <p className="text-sm text-gray-500 mt-4">※プランや予約サイトにより条件が異なる場合は、その条件が優先されます。</p>
          </div>

          {/* セクション⑤：お問い合わせ */}
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <i className="ri-customer-service-2-line w-6 h-6 flex items-center justify-center text-teal-500 mr-2"></i>
              <h3 className="text-xl font-bold text-gray-900">お問い合わせ</h3>
            </div>
            <p className="text-gray-700 mb-4">ご不明な点がありましたら、下記よりお気軽にお問い合わせください。</p>
            
            {/* SNSボタン */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <a 
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 bg-green-500 hover:bg-green-600 active:scale-[0.97] text-white h-11 rounded-3xl text-xs font-semibold transition-all shadow-md whitespace-nowrap cursor-pointer flex-1 max-w-[120px]"
              >
                <i className="ri-line-fill text-sm"></i>
                LINE
              </a>
              <button
                onClick={() => alert(`WeChat ID: ${wechatId}\n\nこのIDで検索して友達追加してください`)}
                className="flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 active:scale-[0.97] text-white h-11 rounded-3xl text-xs font-semibold transition-all shadow-md whitespace-nowrap cursor-pointer flex-1 max-w-[120px]"
              >
                <i className="ri-wechat-fill text-sm"></i>
                WeChat
              </button>
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1 bg-green-700 hover:bg-green-800 active:scale-[0.97] text-white h-11 rounded-3xl text-xs font-semibold transition-all shadow-md whitespace-nowrap cursor-pointer flex-1 max-w-[120px]"
              >
                <i className="ri-whatsapp-fill text-sm"></i>
                WhatsApp
              </a>
            </div>

            <div className="space-y-2 text-gray-700">
              <div className="flex items-center">
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-teal-500 mr-2"></i>
                <a href="mailto:guesthouse017osaka@gmail.com" className="text-teal-600 hover:underline">guesthouse017osaka@gmail.com</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={roomSelectionRef} className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">部屋タイプを選択</h2>
          
          {/* 横スライド式カードコンテナ */}
          <div className="relative">
            <div className="overflow-x-auto pb-4 scrollbar-hide" style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
              <div className="flex gap-4 px-2">
                {roomTypes.map((room) => (
                  <div
                    key={room.id}
                    onClick={() => setSelectedRoom(room)}
                    className={`flex-shrink-0 w-[85vw] sm:w-[400px] bg-white rounded-xl cursor-pointer transition-all duration-300 border-2 ${
                      selectedRoom?.id === room.id
                        ? 'border-teal-500 shadow-xl'
                        : 'border-gray-200 hover:border-teal-300 hover:shadow-lg'
                    }`}
                    style={{ scrollSnapAlign: 'center' }}
                  >
                    {/* 画像エリア(16:9) */}
                    <div className="relative w-full pt-[56.25%] bg-gray-200 rounded-t-xl overflow-hidden">
                      {room.id === '6rooms' ? (
                        <div className="absolute inset-0">
                          <img 
                            src="https://static.readdy.ai/image/532992a9a7b892cdbfcbe570165d1ee5/8c3ce2281945a9b19763f34511465896.jpeg"
                            alt="1棟6室貸切タイプ - 全6部屋"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : room.id === '4rooms' ? (
                        <div className="absolute inset-0">
                          <img 
                            src="https://static.readdy.ai/image/532992a9a7b892cdbfcbe570165d1ee5/fa1bb6ff366eb5259c1221e6ef203fbd.jpeg"
                            alt="1棟4室貸切タイプ"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : room.id === '3rooms' ? (
                        <div className="absolute inset-0">
                          <img 
                            src="https://static.readdy.ai/image/532992a9a7b892cdbfcbe570165d1ee5/6215a8441e7e819127df009fc0a09655.jpeg"
                            alt="1棟3室貸切タイプ"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <i className="ri-image-line text-5xl text-gray-400 mb-2"></i>
                            <p className="text-sm text-gray-500">部屋写真</p>
                          </div>
                        </div>
                      )}
                      {/* 選択チェックマーク */}
                      {selectedRoom?.id === room.id && (
                        <div className="absolute top-3 right-3 w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center shadow-lg">
                          <i className="ri-check-line text-2xl text-white"></i>
                        </div>
                      )}
                    </div>

                    {/* カード内容 */}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{room.name}</h3>
                      
                      <div className="space-y-3 text-sm text-gray-600">
                        <div className="flex items-center">
                          <i className="ri-group-line w-5 h-5 flex items-center justify-center mr-3 text-teal-500"></i>
                          <span className="font-medium">{room.minGuests}〜{room.maxGuests}名</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-door-line w-5 h-5 flex items-center justify-center mr-3 text-gray-400"></i>
                          <span>トイレ{room.toilets}室</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-contrast-drop-line w-5 h-5 flex items-center justify-center mr-3 text-gray-400"></i>
                          <span>シャワー{room.showers}室</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-water-flash-line w-5 h-5 flex items-center justify-center mr-3 text-gray-400"></i>
                          <span>バスルーム{room.baths}室</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-building-line w-5 h-5 flex items-center justify-center mr-3 text-gray-400"></i>
                          <span>{room.floors}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-8 text-gray-900">予約情報入力</h2>

            {selectedRoom && disabledDates.length > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <i className="ri-information-line w-5 h-5 flex items-center justify-center text-yellow-600 mr-2 mt-0.5"></i>
                  <p className="text-sm text-yellow-800">このお部屋タイプには予約できない日があります。カレンダーで確認してください。</p>
                </div>
              </div>
            )}

            {/* 最小宿泊日数の案内 */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <i className="ri-information-line w-5 h-5 flex items-center justify-center text-blue-600 mr-2 mt-0.5"></i>
                <p className="text-sm text-blue-800">特区民泊のため、最低2泊からのご予約となります。</p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                チェックイン日<span className="text-red-500 ml-1">*</span>
              </label>
              <CustomDatePicker
                selected={checkInDate}
                onChange={handleCheckInChange}
                minDate={today}
                excludeDates={excludedDates}
                placeholderText="チェックイン日を選択"
                locale="ja"
              />
              {selectedRoom && disabledDates.length > 0 && (
                <div className="mt-3 p-3 bg-pink-50 border border-pink-200 rounded-lg">
                  <div className="flex items-start text-sm text-gray-700">
                    <i className="ri-close-circle-fill w-4 h-4 flex items-center justify-center text-red-500 mr-2 mt-0.5"></i>
                    <span className="flex-1">
                      <strong className="text-red-600">×</strong> の日は満室のためご予約いただけません
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                チェックアウト日<span className="text-red-500 ml-1">*</span>
              </label>
              <CustomDatePicker
                selected={checkOutDate}
                onChange={handleCheckOutChange}
                minDate={checkInDate || today}
                excludeDates={excludedDates}
                placeholderText="チェックアウト日を選択"
                disabled={!checkInDate}
                locale="ja"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                宿泊人数<span className="text-red-500 ml-1">*</span>
                {selectedRoom && (
                  <span className="text-gray-500 text-xs ml-2">
                    ({selectedRoom.minGuests}〜{selectedRoom.maxGuests}名)
                  </span>
                )}
              </label>
              <input
                type="number"
                required
                min={selectedRoom?.minGuests || 1}
                max={selectedRoom?.maxGuests || 100}
                value={guestCount}
                onChange={(e) => setGuestCount(e.target.value === '' ? '' : parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="人数を入力してください"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                お名前<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                required
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="山田太郎"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                メールアドレス<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="email"
                required
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="example@email.com"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                電話番号<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="tel"
                required
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="090-1234-5678"
              />
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">ご要望・ご質問</label>
              <textarea
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                placeholder="ご要望やご質問がございましたらご記入ください"
              />
              <p className="text-xs text-gray-500 mt-1">{specialRequests.length}/500</p>
            </div>

            {errorMessage && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !selectedRoom}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-4 rounded-lg font-bold text-lg hover:from-teal-600 hover:to-cyan-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? '送信中...' : 'ご予約はこちらから'}
            </button>
          </form>
        </div>
      </section>

      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-4xl text-teal-500"></i>
            </div>
            <h3 className="text-2xl font-bold mb-2 text-gray-900">予約を受け付けました</h3>
            <p className="text-gray-600 mb-6">ご予約ありがとうございます。確認後、担当者よりご連絡させていただきます</p>
            <button
              onClick={() => setShowSuccess(false)}
              className="bg-teal-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
